/**
 * PublicLinksDashboard - Dashboard centralisé des liens publics
 * Design System v2 (Volvo-inspired) - Matching CreditNotes style
 *
 * Affiche tous les liens publics actifs (devis + factures) avec:
 * - Statistiques globales
 * - Liste des liens avec filtres
 * - Actions de révocation
 * - Indicateurs d'expiration proche
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { publicLinksAPI } from '@/utils/api';
import { toFrench } from '@/utils/dateUtils';
import {
  Link as LinkIcon,
  FileText,
  Receipt,
  Eye,
  Clock,
  AlertTriangle,
  Trash2,
  ExternalLink,
  Copy,
  Check,
  Search,
  Filter,
  RefreshCw,
  TrendingUp,
} from 'lucide-react';
import { logger } from '@/utils/logger';
import { showError } from '@/lib/notifications';
import { confirm } from '@/stores/confirmStore';

interface PublicLink {
  id: string;
  token: string;
  documentId: string;
  documentType: 'quote' | 'invoice';
  documentNumber: string;
  documentTitle: string;
  clientName: string;
  amount: number;
  status: string;
  viewCount: number;
  lastViewedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  isExpiringSoon: boolean;
}

interface DashboardStats {
  totalQuoteLinks: number;
  activeQuoteLinks: number;
  totalInvoiceLinks: number;
  activeInvoiceLinks: number;
  totalViews: number;
  expiringSoon: number;
}

interface DashboardData {
  quotes: PublicLink[];
  invoices: PublicLink[];
  stats: DashboardStats;
}

type FilterType = 'all' | 'quotes' | 'invoices' | 'expiring';

export const PublicLinksDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);

  const loadDashboard = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await publicLinksAPI.getDashboard();
      setData(response.data);
    } catch (err: any) {
      logger.error('Erreur chargement dashboard liens publics:', err);
      setError(t('billing.publicLinks.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return t('billing.publicLinks.neverViewed');
    return toFrench(dateString);
  };

  const getDaysUntilExpiry = (expiresAt: string | null) => {
    if (!expiresAt) return null;
    const now = new Date();
    const expiry = new Date(expiresAt);
    const days = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return days;
  };

  const copyToClipboard = async (token: string) => {
    const url = `${window.location.origin}/public/quote/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2000);
    } catch (err) {
      logger.error('Erreur copie:', err);
    }
  };

  const handleRevoke = async (token: string) => {
    const confirmed = await confirm({
      title: t('billing.publicLinks.revokeTitle'),
      message: t('billing.publicLinks.revokeMessage'),
      variant: 'danger',
      confirmText: t('billing.publicLinks.revokeConfirm'),
      cancelText: t('common.actions.cancel'),
    });
    if (!confirmed) {
      return;
    }

    setRevoking(token);
    try {
      await publicLinksAPI.revokeLink(token);
      await loadDashboard();
    } catch (err: any) {
      logger.error('Erreur révocation:', err);
      showError(t('billing.publicLinks.revokeError'));
    } finally {
      setRevoking(null);
    }
  };

  const allLinks = useMemo(() => {
    if (!data) return [];
    return [...data.quotes, ...data.invoices];
  }, [data]);

  const filteredLinks = useMemo(() => {
    let links = allLinks;

    // Filtre par type
    if (filter === 'quotes') {
      links = links.filter((l) => l.documentType === 'quote');
    } else if (filter === 'invoices') {
      links = links.filter((l) => l.documentType === 'invoice');
    } else if (filter === 'expiring') {
      links = links.filter((l) => l.isExpiringSoon);
    }

    // Filtre par recherche
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      links = links.filter(
        (l) =>
          l.documentNumber.toLowerCase().includes(term) ||
          l.clientName.toLowerCase().includes(term) ||
          l.documentTitle?.toLowerCase().includes(term)
      );
    }

    return links.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [allLinks, filter, searchTerm]);

  if (loading) {
    return (
      <div className="px-8 pt-8 pb-12">
        <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 p-12">
          <div className="flex flex-col items-center justify-center">
            <div className="animate-spin w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full" />
            <p className="text-gray-600 mt-4">{t('billing.publicLinks.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-8 pt-8 pb-12">
        <div className="bg-red-50/50 backdrop-blur-xl rounded-3xl border border-red-200 p-6">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-red-500" />
            <div>
              <h3 className="font-semibold text-red-700">{t('common.error')}</h3>
              <p className="text-red-600">{error}</p>
            </div>
          </div>
          <button
            onClick={loadDashboard}
            className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all duration-300 hover:shadow-lg"
          >
            {t('common.actions.retry')}
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { stats } = data;

  return (
    <div className="px-8 pt-8 pb-12 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-4xl sm:text-5xl font-light tracking-tight text-gray-900 flex items-center gap-3">
            <LinkIcon className="w-10 h-10 text-teal-600" />
            {t('billing.publicLinks.title')}
          </h2>
          <p className="text-base text-gray-500 font-light tracking-wide mt-2">
            {t('billing.publicLinks.subtitle')}
          </p>
        </div>
        <button
          onClick={loadDashboard}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/40 border border-white/50 rounded-xl font-medium text-gray-700
                     hover:bg-white hover:border-gray-300 hover:shadow-lg hover:shadow-gray-100/50
                     active:scale-[0.98] transition-all duration-300"
        >
          <RefreshCw className="w-4 h-4" />
          {t('billing.publicLinks.refresh')}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 p-5
                        hover:bg-white/30 hover:shadow-2xl hover:shadow-blue-100/30 transition-all duration-300 group">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-100 rounded-xl group-hover:bg-blue-200 transition-colors duration-300">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <span className="text-sm font-medium text-gray-500">{t('billing.publicLinks.statsQuotes')}</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats.activeQuoteLinks}</p>
          <p className="text-xs text-gray-400 mt-1">{t('billing.publicLinks.activeLinks')}</p>
        </div>

        <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 p-5
                        hover:bg-white/30 hover:shadow-2xl hover:shadow-green-100/30 transition-all duration-300 group">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-green-100 rounded-xl group-hover:bg-green-200 transition-colors duration-300">
              <Receipt className="w-5 h-5 text-green-600" />
            </div>
            <span className="text-sm font-medium text-gray-500">{t('billing.publicLinks.statsInvoices')}</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats.activeInvoiceLinks}</p>
          <p className="text-xs text-gray-400 mt-1">{t('billing.publicLinks.activeLinks')}</p>
        </div>

        <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 p-5
                        hover:bg-white/30 hover:shadow-2xl hover:shadow-purple-100/30 transition-all duration-300 group">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-purple-100 rounded-xl group-hover:bg-purple-200 transition-colors duration-300">
              <Eye className="w-5 h-5 text-purple-600" />
            </div>
            <span className="text-sm font-medium text-gray-500">{t('billing.publicLinks.statsTotalViews')}</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats.totalViews}</p>
          <p className="text-xs text-gray-400 mt-1">{t('billing.publicLinks.consultations')}</p>
        </div>

        <div className={`backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 p-5
                        transition-all duration-300 group ${
          stats.expiringSoon > 0
            ? 'bg-orange-50/50 border border-orange-200 hover:bg-orange-100/50 hover:shadow-2xl hover:shadow-orange-100/50'
            : 'bg-white/20 border border-white/30 hover:bg-white/30'
        }`}>
          <div className="flex items-center gap-3 mb-2">
            <div className={`p-2 rounded-xl transition-colors duration-300 ${
              stats.expiringSoon > 0 ? 'bg-orange-200 group-hover:bg-orange-300' : 'bg-gray-100 group-hover:bg-gray-200'
            }`}>
              <AlertTriangle className={`w-5 h-5 ${stats.expiringSoon > 0 ? 'text-orange-600' : 'text-gray-500'}`} />
            </div>
            <span className="text-sm font-medium text-gray-500">{t('billing.publicLinks.statsExpiringSoon')}</span>
          </div>
          <p className={`text-3xl font-bold ${stats.expiringSoon > 0 ? 'text-orange-600' : 'text-gray-900'}`}>
            {stats.expiringSoon}
          </p>
          <p className="text-xs text-gray-400 mt-1">{t('billing.publicLinks.inDays')}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1 group">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400
                               group-focus-within:text-teal-500 transition-colors duration-300" />
            <input
              type="text"
              placeholder={t('billing.publicLinks.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white/40 border border-white/50 rounded-xl
                         focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white
                         hover:bg-white/60 hover:border-teal-200 transition-all duration-300"
            />
          </div>

          {/* Filter buttons */}
          <div className="flex gap-2 flex-wrap">
            {[
              { value: 'all', label: t('billing.publicLinks.filterAll'), icon: Filter },
              { value: 'quotes', label: t('billing.publicLinks.filterQuotes'), icon: FileText },
              { value: 'invoices', label: t('billing.publicLinks.filterInvoices'), icon: Receipt },
              { value: 'expiring', label: t('billing.publicLinks.filterExpiring'), icon: Clock },
            ].map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                onClick={() => setFilter(value as FilterType)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all duration-300 ${
                  filter === value
                    ? 'bg-teal-600 text-white shadow-lg shadow-teal-200/50 scale-[1.02]'
                    : 'bg-white/40 border border-white/50 text-gray-700 hover:bg-white hover:border-teal-200 hover:shadow-md'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Links List */}
      {filteredLinks.length === 0 ? (
        <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 p-12 text-center">
          <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <LinkIcon className="w-8 h-8 text-teal-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('billing.publicLinks.noLinksFound')}</h3>
          <p className="text-gray-500">
            {searchTerm
              ? t('billing.publicLinks.noSearchResults')
              : t('billing.publicLinks.createFromDocument')}
          </p>
        </div>
      ) : (
        <div className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 overflow-hidden">
          <div className="divide-y divide-white/20">
            {filteredLinks.map((link) => {
              const daysLeft = getDaysUntilExpiry(link.expiresAt);
              const isExpired = daysLeft !== null && daysLeft <= 0;

              return (
                <div
                  key={link.id}
                  className={`p-5 bg-white/10 hover:bg-white hover:shadow-lg hover:shadow-teal-100/30
                             transition-all duration-300 group border-l-4 border-transparent
                             hover:border-l-teal-500 ${isExpired ? 'opacity-50' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {/* Type Icon */}
                      <div
                        className={`p-2.5 rounded-xl transition-all duration-300 ${
                          link.documentType === 'quote'
                            ? 'bg-blue-100 text-blue-600 group-hover:bg-blue-200'
                            : 'bg-green-100 text-green-600 group-hover:bg-green-200'
                        }`}
                      >
                        {link.documentType === 'quote' ? (
                          <FileText className="w-5 h-5" />
                        ) : (
                          <Receipt className="w-5 h-5" />
                        )}
                      </div>

                      {/* Info */}
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900 group-hover:text-teal-700 transition-colors duration-300">
                            {link.documentNumber}
                          </h3>
                          {link.isExpiringSoon && !isExpired && (
                            <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                              {t('billing.publicLinks.expiresInDays', { days: daysLeft })}
                            </span>
                          )}
                          {isExpired && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                              {t('billing.publicLinks.expired')}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{link.clientName}</p>
                      </div>
                    </div>

                    {/* Stats & Actions */}
                    <div className="flex items-center gap-6">
                      {/* Amount */}
                      <div className="text-right hidden md:block">
                        <p className="font-semibold text-gray-900">{formatAmount(link.amount)}</p>
                        <p className="text-xs text-gray-500">{t('billing.publicLinks.amountTTC')}</p>
                      </div>

                      {/* Views */}
                      <div className="flex items-center gap-2 text-gray-500 group-hover:text-teal-600 transition-colors">
                        <Eye className="w-4 h-4" />
                        <span className="text-sm font-medium">{link.viewCount}</span>
                      </div>

                      {/* Last viewed */}
                      <div className="hidden lg:block text-right">
                        <p className="text-xs text-gray-500">{t('billing.publicLinks.lastView')}</p>
                        <p className="text-sm font-medium text-gray-700">
                          {formatDate(link.lastViewedAt)}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => copyToClipboard(link.token)}
                          className="p-2.5 bg-white/40 border border-white/50 rounded-xl text-gray-500
                                     hover:bg-white hover:text-teal-600 hover:border-teal-200 hover:shadow-md
                                     active:scale-95 transition-all duration-300"
                          title={t('billing.publicLinks.copyLink')}
                        >
                          {copiedToken === link.token ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>

                        <a
                          href={`/public/${link.documentType === 'quote' ? 'quote' : 'invoice'}/${link.token}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2.5 bg-white/40 border border-white/50 rounded-xl text-gray-500
                                     hover:bg-white hover:text-blue-600 hover:border-blue-200 hover:shadow-md
                                     active:scale-95 transition-all duration-300"
                          title={t('billing.publicLinks.openLink')}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>

                        <button
                          onClick={() => handleRevoke(link.token)}
                          disabled={revoking === link.token}
                          className="p-2.5 bg-white/40 border border-white/50 rounded-xl text-gray-500
                                     hover:bg-red-50 hover:text-red-600 hover:border-red-200 hover:shadow-md
                                     active:scale-95 transition-all duration-300 disabled:opacity-50"
                          title={t('billing.publicLinks.revokeLink')}
                        >
                          {revoking === link.token ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Summary footer */}
      {filteredLinks.length > 0 && (
        <div className="flex items-center justify-between text-sm text-gray-500 px-2">
          <p>
            {t('billing.publicLinks.linksShown', { count: filteredLinks.length })}
          </p>
          <p className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-teal-500" />
            {t('billing.publicLinks.totalViewsLabel')} <span className="font-semibold text-gray-700">{filteredLinks.reduce((sum, l) => sum + l.viewCount, 0)}</span>
          </p>
        </div>
      )}
    </div>
  );
};

export default PublicLinksDashboard;
